import { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar/AppBar';
import { CSSObject, styled, Theme } from '@mui/material/styles';
import MuiAppBar from '@mui/material/AppBar';
import MuiDrawer from '@mui/material/Drawer';
import {
    ACCOUNTS,
    CASES_GENERAL,
    CASES_GENERAL_INFORMATION,
    HOME,
    PQR,
    REDIRECTLIST,
    SARLAFT,
    LEGAL_PROCEEDINGS,
    USERLIST,
    SAMPLES,
    ORBIT,
    ORBIT_HISTORY,
    PQR_HISTORY,
    INITIAL_CAUSALITY,
    FINAL_CAUSALITY,
    MOTIVE,
    PROCEDURE,
    PQR_TYPE,
    SUBTYPE_DP,
    SWITCH,
    QUOTATION,
    EMAIL_SENDING,
    SUBMOTIVE1,
    SUBMOTIVE2,
    SUBMOTIVE3,
    SUBMOTIVE4,
    PQR_RECEPTION_CHANNEL,
    PQR_RECEIVED_THROUGH,
    PRODUCT,
    REDIRECT_AREA,
    FRAUD_MODE,
    FRAUD_TYPE,
    PQR_TIMES,
    FUNCTIONARIES_SWITCHBOARD,
    COMPLIANCE_TERMS,
    ALERTS,
    SURVEYS,
    SURVEYS_RESPONSES,
} from '../../../routes/paths';
import { ModulesMessages } from '../../../shared/messages/modulesMessages';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import AppsIcon from '@mui/icons-material/Apps';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import PqrIcon from '@mui/icons-material/FlightClass';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import EscalatorWarningIcon from '@mui/icons-material/EscalatorWarning';
import NotesIcon from '@mui/icons-material/Notes';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import GavelIcon from '@mui/icons-material/Gavel';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import InboxIcon from '@mui/icons-material/Inbox';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import SpaIcon from '@mui/icons-material/Spa';
import CategoryIcon from '@mui/icons-material/Category';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ExitToApp from '@mui/icons-material/ExitToApp';
import ViewListIcon from '@mui/icons-material/ViewList';
import RepartitionIcon from '@mui/icons-material/Repartition';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import EmailIcon from '@mui/icons-material/Email';
import ListAltIcon from '@mui/icons-material/ListAlt';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import AssistantPhotoIcon from '@mui/icons-material/AssistantPhoto';
import RedoIcon from '@mui/icons-material/Redo';
import PersonPinIcon from '@mui/icons-material/PersonPin';
import BusinessIcon from '@mui/icons-material/Business';
import ReportIcon from '@mui/icons-material/Report';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SatelliteAltIcon from '@mui/icons-material/SatelliteAlt';

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}

export const drawerWidth = 300;

export const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    height: '100%',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
});

export const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    height: '100%',
    width: `calc(${theme.spacing(9)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(10)} + 1px)`,
    },
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
});

export const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
}));

export const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    borderColor: theme.palette.primary.contrastText,
    ...(open && {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
    }),
}));

export const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    borderColor: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        borderColor: theme.palette.primary.contrastText,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const {
    HOME_MODULE,
    MODULES_MODULE,
    ACCOUNTS_PAGE,
    PQR_PAGE,
    PQR_HISTORY_PAGE,
    GENERAL_PAGE,
    SARLAFT_PAGE,
    REDIRECT_MODULE,
    REDIRECT_PERSON_PAGE,
    REDIRECT_AREA_PAGE,
    USERS_MODULE,
    CASES_PAGE,
    MODULES_PQR_MANAGEMENT,
    LEGAL_PROCEEDING,
    ALERTS_MODULE,
    SAMPLES_MODULE,
    MODULES_TYPIFICATIONS,
    FINAL_CAUSALITY_MODULE,
    INITIAL_CAUSALITY_PAGE,
    SYSTEM_CONFIGURATION_MODULE,
    MOTIVE_PAGE,
    SUBMOTIVE1_PAGE,
    SUBMOTIVE2_PAGE,
    SUBMOTIVE3_PAGE,
    SUBMOTIVE4_PAGE,
    PROCEDURE_PAGE,
    PQR_TYPE_PAGE,
    SUBTYPE_DP_PAGE,
    SWITCH_PAGE,
    QUOTATION_PAGE,
    EMAIL_SENDING_PAGE,
    PQR_RECPETION_CHANNEL_PAGE,
    PQR_RECEIVED_THROUGH_PAGE,
    PRODUCT_PAGE,
    FRAUD_MODE_PAGE,
    FRAUD_TYPE_PAGE,
    PQR_STATUS_PAGE,
    FUNCTIONARIES_SWITCHBOARD_PAGE,
    COMPLIANCE_TERMS_PAGE,
    SURVEYS_PAGE,
    SURVEY_RESPONSES_PAGE,
    ORBIT_MODULE,
    ORBIT_RADICATION_PAGE,
    ORBIT_HISTORY_PAGE,
} = ModulesMessages;

const ORBIT_SIDEBAR_PERMISSIONS = [
    'admin',
    'agent',
    'backoffice',
    'supervisor',
    'formador',
    'calidad',
    'cliente',
    'supervisor-junior',
    'agente-conmutador',
    'backoffice-cotizador',
];

export const ModulesSideBar = [
    {
        route: HOME,
        label: HOME_MODULE,
        icon: <HomeRoundedIcon sx={{ color: 'primary.contrastText' }} />,
    },
    {
        label: MODULES_MODULE,
        icon: <AppsIcon sx={{ color: 'primary.contrastText' }} />,
        permissions: [
            'admin',
            'agent',
            'backoffice',
            'supervisor',
            'formador',
            'calidad',
            'cliente',
            'supervisor-junior',
            'agente-conmutador',
            'backoffice-cotizador',
        ],
        subItems: [
            {
                route: ACCOUNTS,
                label: ACCOUNTS_PAGE,
                icon: <AccountBoxIcon sx={{ color: 'primary.contrastText' }} />,
                permissions: [
                    'admin',
                    'agent',
                    'backoffice',
                    'supervisor',
                    'formador',
                    'calidad',
                    'cliente',
                    'supervisor-junior',
                    'agente-conmutador',
                    'backoffice-cotizador',
                ],
            },
            {
                route: CASES_GENERAL_INFORMATION,
                label: GENERAL_PAGE,
                icon: (
                    <FormatListBulletedIcon
                        sx={{ color: 'primary.contrastText' }}
                    />
                ),
                permissions: [
                    'admin',
                    'agent',
                    'backoffice',
                    'supervisor',
                    'formador',
                    'calidad',
                    'cliente',
                    'supervisor-junior',
                    'agente-conmutador',
                ],
            },
            {
                route: SWITCH,
                label: SWITCH_PAGE,
                icon: (
                    <RepartitionIcon sx={{ color: 'primary.contrastText' }} />
                ),
                permissions: [
                    'admin',
                    'agent',
                    'backoffice',
                    'supervisor',
                    'formador',
                    'calidad',
                    'cliente',
                    'supervisor-junior',
                    'agente-conmutador',
                ],
            },
            {
                route: PQR,
                label: PQR_PAGE,
                icon: <PqrIcon sx={{ color: 'primary.contrastText' }} />,
                permissions: [
                    'admin',
                    'agent',
                    'backoffice',
                    'supervisor',
                    'formador',
                    'calidad',
                    'cliente',
                    'supervisor-junior',
                    'agente-conmutador',
                ],
            },
            {
                route: PQR_HISTORY,
                label: PQR_HISTORY_PAGE,
                icon: (
                    <ManageSearchIcon sx={{ color: 'primary.contrastText' }} />
                ),
                permissions: [
                    'admin',
                    'agent',
                    'backoffice',
                    'supervisor',
                    'formador',
                    'calidad',
                    'cliente',
                    'supervisor-junior',
                    'agente-conmutador',
                ],
            },
            {
                route: SARLAFT,
                label: SARLAFT_PAGE,
                icon: (
                    <EscalatorWarningIcon
                        sx={{ color: 'primary.contrastText' }}
                    />
                ),
                permissions: [
                    'admin',
                    'agent',
                    'backoffice',
                    'supervisor',
                    'formador',
                    'calidad',
                    'cliente',
                    'supervisor-junior',
                    'agente-conmutador',
                ],
            },
            {
                route: CASES_GENERAL,
                label: CASES_PAGE,
                icon: <NotesIcon sx={{ color: 'primary.contrastText' }} />,
                permissions: [
                    'admin',
                    'agent',
                    'backoffice',
                    'supervisor',
                    'formador',
                    'calidad',
                    'cliente',
                    'supervisor-junior',
                    'agente-conmutador',
                ],
            },
            {
                route: QUOTATION,
                label: QUOTATION_PAGE,
                icon: (
                    <ShoppingCartIcon sx={{ color: 'primary.contrastText' }} />
                ),
                permissions: [
                    'admin',
                    'agent',
                    'backoffice',
                    'supervisor',
                    'formador',
                    'calidad',
                    'cliente',
                    'supervisor-junior',
                    'agente-conmutador',
                    'backoffice-cotizador',
                ],
            },
            {
                route: EMAIL_SENDING,
                label: EMAIL_SENDING_PAGE,
                icon: <EmailIcon sx={{ color: 'primary.contrastText' }} />,
                permissions: [
                    'admin',
                    'agent',
                    'backoffice',
                    'supervisor',
                    'formador',
                    'calidad',
                    'cliente',
                    'supervisor-junior',
                    'agente-conmutador',
                    'backoffice-cotizador',
                ],
            },
        ],
    },
    // {
    //     route: EXPORT,
    //     label: EXPORT_MODULE,
    //     icon: <DownloadIcon sx={{ color: 'primary.contrastText' }} />,
    //     permissions: ['admin'],
    // },
    {
        label: MODULES_PQR_MANAGEMENT,
        icon: <HeadsetMicIcon sx={{ color: 'primary.contrastText' }} />,
        permissions: [
            'admin',
            'agent',
            'backoffice',
            'supervisor',
            'formador',
            'calidad',
            'cliente',
            'agente-conmutador',
        ],
        subItems: [
            {
                route: LEGAL_PROCEEDINGS,
                label: LEGAL_PROCEEDING,
                icon: <GavelIcon sx={{ color: 'primary.contrastText' }} />,
                permissions: [
                    'admin',
                    'agent',
                    'backoffice',
                    'supervisor',
                    'formador',
                    'calidad',
                    'cliente',
                    'agente-conmutador',
                ],
            },
            {
                route: ALERTS,
                label: ALERTS_MODULE,
                icon: (
                    <NotificationsActiveIcon
                        sx={{ color: 'primary.contrastText' }}
                    />
                ),
                permissions: [
                    'admin',
                    'agent',
                    'backoffice',
                    'supervisor',
                    'formador',
                    'calidad',
                    'cliente',
                    'agente-conmutador',
                ],
            },
            {
                route: SAMPLES,
                label: SAMPLES_MODULE,
                icon: <InboxIcon sx={{ color: 'primary.contrastText' }} />,
                permissions: [
                    'admin',
                    'agent',
                    'backoffice',
                    'supervisor',
                    'formador',
                    'calidad',
                    'cliente',
                    'agente-conmutador',
                ],
            },
        ],
    },
    {
        label: MODULES_TYPIFICATIONS,
        icon: <CategoryIcon sx={{ color: 'primary.contrastText' }} />,
        permissions: ['admin', 'supervisor', 'formador', 'calidad', 'cliente'],
        subItems: [
            {
                route: INITIAL_CAUSALITY,
                label: INITIAL_CAUSALITY_PAGE,
                icon: <SpaIcon sx={{ color: 'primary.contrastText' }} />,
                permissions: [
                    'admin',
                    'supervisor',
                    'formador',
                    'calidad',
                    'cliente',
                ],
            },
            {
                route: FINAL_CAUSALITY,
                label: FINAL_CAUSALITY_MODULE,
                icon: (
                    <CheckCircleIcon sx={{ color: 'primary.contrastText' }} />
                ),
                permissions: [
                    'admin',
                    'supervisor',
                    'formador',
                    'calidad',
                    'cliente',
                ],
            },
        ],
    },
    {
        label: REDIRECT_MODULE,
        icon: <RedoIcon sx={{ color: 'primary.contrastText' }} />,
        permissions: [
            'admin',
            'agent',
            'backoffice',
            'supervisor',
            'formador',
            'calidad',
            'cliente',
            'supervisor-junior',
        ],
        subItems: [
            {
                route: REDIRECTLIST,
                label: REDIRECT_PERSON_PAGE,
                icon: <PersonPinIcon sx={{ color: 'primary.contrastText' }} />,
                permissions: [
                    'admin',
                    'supervisor',
                    'formador',
                    'agent',
                    'backoffice',
                    'calidad',
                    'cliente',
                    'supervisor-junior',
                ],
            },
            {
                route: REDIRECT_AREA,
                label: REDIRECT_AREA_PAGE,
                icon: <BusinessIcon sx={{ color: 'primary.contrastText' }} />,
                permissions: [
                    'admin',
                    'supervisor',
                    'formador',
                    'agent',
                    'backoffice',
                    'calidad',
                    'cliente',
                    'supervisor-junior',
                ],
            },
        ],
    },
    {
        label: SYSTEM_CONFIGURATION_MODULE,
        icon: <SettingsIcon sx={{ color: 'primary.contrastText' }} />,
        permissions: [
            'admin',
            'supervisor',
            'formador',
            'calidad',
            'cliente',
            'agente-conmutador',
        ],
        subItems: [
            {
                route: MOTIVE,
                label: MOTIVE_PAGE,
                icon: <ChecklistIcon sx={{ color: 'primary.contrastText' }} />,
                permissions: [
                    'admin',
                    'supervisor',
                    'formador',
                    'calidad',
                    'cliente',
                ],
            },
            {
                route: SUBMOTIVE1,
                label: SUBMOTIVE1_PAGE,
                icon: <ViewListIcon sx={{ color: 'primary.contrastText' }} />,
                permissions: [
                    'admin',
                    'supervisor',
                    'formador',
                    'calidad',
                    'cliente',
                ],
            },
            {
                route: SUBMOTIVE2,
                label: SUBMOTIVE2_PAGE,
                icon: <NotesIcon sx={{ color: 'primary.contrastText' }} />,
                permissions: [
                    'admin',
                    'supervisor',
                    'formador',
                    'calidad',
                    'cliente',
                ],
            },
            {
                route: SUBMOTIVE3,
                label: SUBMOTIVE3_PAGE,
                icon: (
                    <FormatListNumberedIcon
                        sx={{ color: 'primary.contrastText' }}
                    />
                ),
                permissions: [
                    'admin',
                    'supervisor',
                    'formador',
                    'calidad',
                    'cliente',
                ],
            },
            {
                route: SUBMOTIVE4,
                label: SUBMOTIVE4_PAGE,
                icon: <ListAltIcon sx={{ color: 'primary.contrastText' }} />,
                permissions: [
                    'admin',
                    'supervisor',
                    'formador',
                    'calidad',
                    'cliente',
                ],
            },
            {
                route: PQR_TYPE,
                label: PQR_TYPE_PAGE,
                icon: (
                    <FormatListBulletedIcon
                        sx={{ color: 'primary.contrastText' }}
                    />
                ),
                permissions: [
                    'admin',
                    'supervisor',
                    'formador',
                    'calidad',
                    'cliente',
                ],
            },
            {
                route: SUBTYPE_DP,
                label: SUBTYPE_DP_PAGE,
                icon: (
                    <ManageSearchIcon sx={{ color: 'primary.contrastText' }} />
                ),
                permissions: [
                    'admin',
                    'supervisor',
                    'formador',
                    'calidad',
                    'cliente',
                ],
            },
            {
                route: PROCEDURE,
                label: PROCEDURE_PAGE,
                icon: <ExitToApp sx={{ color: 'primary.contrastText' }} />,
                permissions: ['admin', 'supervisor', 'formador', 'calidad'],
            },
            {
                route: PQR_RECEPTION_CHANNEL,
                label: PQR_RECPETION_CHANNEL_PAGE,
                icon: (
                    <SupportAgentIcon sx={{ color: 'primary.contrastText' }} />
                ),
                permissions: ['admin', 'supervisor', 'formador', 'calidad'],
            },
            {
                route: PQR_RECEIVED_THROUGH,
                label: PQR_RECEIVED_THROUGH_PAGE,
                icon: <InboxIcon sx={{ color: 'primary.contrastText' }} />,
                permissions: ['admin', 'supervisor', 'formador', 'calidad'],
            },
            {
                route: PRODUCT,
                label: PRODUCT_PAGE,
                icon: (
                    <AssistantPhotoIcon
                        sx={{ color: 'primary.contrastText' }}
                    />
                ),
                permissions: ['admin', 'supervisor', 'formador', 'calidad'],
            },
            {
                route: FRAUD_MODE,
                label: FRAUD_MODE_PAGE,
                icon: <ReportIcon sx={{ color: 'primary.contrastText' }} />,
                permissions: ['admin', 'supervisor', 'formador', 'calidad'],
            },
            {
                route: FRAUD_TYPE,
                label: FRAUD_TYPE_PAGE,
                icon: (
                    <ReportGmailerrorredIcon
                        sx={{ color: 'primary.contrastText' }}
                    />
                ),
                permissions: ['admin', 'supervisor', 'formador', 'calidad'],
            },
            {
                route: FUNCTIONARIES_SWITCHBOARD,
                label: FUNCTIONARIES_SWITCHBOARD_PAGE,
                icon: <PersonPinIcon sx={{ color: 'primary.contrastText' }} />,
                permissions: [
                    'admin',
                    'supervisor',
                    'formador',
                    'calidad',
                    'agente-conmutador',
                ],
            },
            {
                route: PQR_TIMES,
                label: PQR_STATUS_PAGE,
                icon: (
                    <EditCalendarIcon sx={{ color: 'primary.contrastText' }} />
                ),
                permissions: ['admin', 'supervisor', 'formador', 'calidad'],
            },
            {
                route: COMPLIANCE_TERMS,
                label: COMPLIANCE_TERMS_PAGE,
                icon: (
                    <AssignmentTurnedInIcon
                        sx={{ color: 'primary.contrastText' }}
                    />
                ),
                permissions: ['admin', 'supervisor', 'formador', 'calidad'],
            },
            {
                route: SURVEYS,
                label: SURVEYS_PAGE,
                icon: <AssignmentIcon sx={{ color: 'primary.contrastText' }} />,
                permissions: ['admin', 'supervisor', 'formador', 'calidad'],
            },
            {
                route: SURVEYS_RESPONSES,
                label: SURVEY_RESPONSES_PAGE,
                icon: (
                    <FormatListBulletedIcon
                        sx={{ color: 'primary.contrastText' }}
                    />
                ),
                permissions: ['admin', 'supervisor', 'formador', 'calidad'],
            },
        ],
    },
    {
        route: USERLIST,
        label: USERS_MODULE,
        icon: <PeopleAltRoundedIcon sx={{ color: 'primary.contrastText' }} />,
        permissions: ['admin', 'supervisor'],
    },
    ...(process.env.REACT_APP_ORBIT_RADICATION_ENABLED === 'true'
        ? [
            {
                label: ORBIT_MODULE,
                icon: (
                    <SatelliteAltIcon
                        sx={{ color: 'primary.contrastText' }}
                    />
                ),
                permissions: ORBIT_SIDEBAR_PERMISSIONS,
                subItems: [
                    {
                        route: ORBIT,
                        label: ORBIT_RADICATION_PAGE,
                        icon: (
                            <AssignmentIcon
                                sx={{ color: 'primary.contrastText' }}
                            />
                        ),
                        permissions: ORBIT_SIDEBAR_PERMISSIONS,
                    },
                    {
                        route: ORBIT_HISTORY,
                        label: ORBIT_HISTORY_PAGE,
                        icon: (
                            <ManageSearchIcon
                                sx={{ color: 'primary.contrastText' }}
                            />
                        ),
                        permissions: ORBIT_SIDEBAR_PERMISSIONS,
                    },
                ],
            },
        ]
        : []),
];
